// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

"use client";

import React, { createContext, useContext, useMemo } from "react";
import type { TraceProApiClient } from "@tracepro/api-client";
import { useGetToken } from "@tracepro/auth";
import { getApiClient } from "./api";

const ApiContext = createContext<TraceProApiClient | null>(null);

/**
 * Build-time prerender stub. Returns a recursive Proxy where property reads
 * yield more proxies, but any function call throws — so prerender of static
 * pages can render without NEXT_PUBLIC_API_URL while real runtime call sites
 * fail loudly if env is mis-configured in production.
 */
function makeStubClient(): TraceProApiClient {
  const handler: ProxyHandler<object> = {
    get(_target, prop) {
      if (prop === "then" || prop === Symbol.toPrimitive || prop === Symbol.iterator) return undefined;
      return new Proxy(function stub() {
        throw new Error("useApi: NEXT_PUBLIC_API_URL not configured (build-time stub)");
      }, handler);
    },
  };
  return new Proxy({}, handler) as TraceProApiClient;
}

export function ApiProvider({ children }: { children: React.ReactNode }) {
  const getToken = useGetToken();
  // Build-time prerender of static error pages (/_not-found) instantiates the
  // provider tree without NEXT_PUBLIC_API_URL. Defer client construction until
  // the env var is present; null context surfaces the same error to useApi
  // callers at runtime if mis-configured in production.
  const api = useMemo<TraceProApiClient | null>(() => {
    try {
      return getApiClient(getToken);
    } catch {
      return null;
    }
  }, [getToken]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): TraceProApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    // During build-time prerender NEXT_PUBLIC_API_URL may be absent. Return a
    // recursive proxy whose property access yields callables that throw only
    // when actually invoked at runtime — so destructure (`const { x } = useApi()`)
    // and method-chain (`api.foo.bar`) succeed at prerender, deferring the
    // failure to the eventual call site after hydration.
    return makeStubClient();
  }
  return ctx;
}
